import type { MediaProps } from '#/lib/sanity';
import type { SanityEventCard } from '#/lib/sanity/queries/eventCard';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';
import { Link } from '#/components/Link';
import { Media } from '#/components/Media';

function EventDateBadge({ startDate }: { startDate: string }) {
  const date = new Date(startDate);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.toLocaleDateString('en-US', { day: '2-digit' });

  return (
    <div className="absolute left-12 top-12 z-10 flex flex-col items-center rounded-8 bg-cloud px-8 py-8 text-center shadow-[4px_4px_10px_rgba(0,0,0,0.15)]">
      {/* type-eyebrow uses text-12; avoid cn() with text-* colors (twMerge drops size) */}
      <span className="type-eyebrow text-cipher">{month}</span>
      <span className="type-heading4 text-charcoal">{day}</span>
    </div>
  );
}

function EventPastBadge() {
  return (
    <span className="absolute right-12 top-12 z-10 inline-flex w-fit items-center rounded-full bg-charcoal px-16 py-6 font-azeret text-[10px] font-medium uppercase leading-100 tracking-8 text-cloud">
      Past event
    </span>
  );
}

function EventMediaOverlays({
  startDate,
  isPast,
}: {
  startDate: string;
  isPast: boolean;
}) {
  return (
    <>
      {isPast ? <EventPastBadge /> : <EventDateBadge startDate={startDate} />}
    </>
  );
}

function EventCategoryBadge({
  title,
  badgeTone,
}: {
  title: string;
  badgeTone: 'electric' | 'cipher';
}) {
  return (
    <span
      className={cn(
        // text-[10px] not text-10: twMerge treats text-10 like a color token
        // and drops it when paired with text-charcoal / text-cloud.
        'inline-flex w-fit items-center self-start rounded-full px-16 py-6 font-azeret text-[10px] font-medium uppercase leading-100 tracking-8',
        badgeTone === 'electric'
          ? 'bg-electric text-charcoal'
          : 'bg-cipher text-cloud',
      )}
    >
      {title}
    </span>
  );
}

function EventCardCopy({ event }: { event: SanityEventCard }) {
  return (
    <>
      <EventCategoryBadge
        title={event.category.title}
        badgeTone={event.category.badgeTone}
      />
      <h3 className="type-heading5 text-pretty text-charcoal">{event.title}</h3>
      <p className="type-paragraph text-pretty text-charcoal">
        {event.excerpt}
      </p>
    </>
  );
}

export function EventCard({
  event,
  mediaSizes,
  variant = 'listing',
}: {
  event: SanityEventCard;
  mediaSizes: MediaProps['sizes'];
  /** `listing` = /events grid; `homepage` = padded white card; `featured` = horizontal 1-up */
  variant?: 'listing' | 'homepage' | 'featured';
}) {
  if (variant === 'featured') {
    return (
      <Link
        href={event.href}
        className="group flex h-full flex-col overflow-hidden rounded-16 bg-cloud m:flex-row"
      >
        <div className="relative m:w-1/2">
          <Media
            media={event.thumbnailImage}
            className="aspect-[16/9] w-full m:aspect-auto m:h-full m:min-h-[304px]"
            mediaClassName="rounded-none object-cover"
            aspect={false}
            placeholder={true}
            sizes={mediaSizes}
          />
          <EventMediaOverlays
            startDate={event.startDate}
            isPast={event.isPast}
          />
        </div>
        <div className="flex flex-1 flex-col gap-32 m:w-1/2">
          <div className="flex flex-col gap-12 px-20 pt-30 m:px-40 m:pt-40">
            <EventCardCopy event={event} />
          </div>
          <div className="mt-auto px-20 py-16 m:px-40">
            <Button variant="link" className="text-charcoal" asDiv>
              {event.ctaLabel}
            </Button>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'homepage') {
    return (
      <Link
        href={event.href}
        className="group flex h-full flex-col overflow-hidden rounded-16 bg-cloud"
      >
        <div className="relative">
          <Media
            media={event.thumbnailImage}
            className="h-[200px] w-full rounded-none"
            mediaClassName="rounded-none object-cover"
            aspect={false}
            placeholder={true}
            sizes={mediaSizes}
          />
          <EventMediaOverlays
            startDate={event.startDate}
            isPast={event.isPast}
          />
        </div>
        <div className="flex flex-1 flex-col gap-12 px-20 pt-30 m:px-40 m:pt-40">
          <EventCardCopy event={event} />
        </div>
        <div className="mt-auto px-20 py-16 m:px-40">
          <Button variant="link" className="text-charcoal" asDiv>
            {event.ctaLabel}
          </Button>
        </div>
      </Link>
    );
  }

  return (
    <Link href={event.href} className="group flex h-full flex-col">
      <div className="relative">
        <Media
          media={event.thumbnailImage}
          className="aspect-[16/9] w-full rounded-16"
          aspect={false}
          placeholder={true}
          sizes={mediaSizes}
        />
        <EventMediaOverlays startDate={event.startDate} isPast={event.isPast} />
      </div>
      <div className="flex flex-1 flex-col gap-12 py-20">
        <EventCardCopy event={event} />
        <Button variant="link" className="mt-auto pt-20 text-charcoal" asDiv>
          {event.ctaLabel}
        </Button>
      </div>
    </Link>
  );
}
